import { useState, type FormEvent } from "react";

interface PaginationProps {
  current: number;
  total: number;
  onChange: (page: number) => void;
}

function navClass(disabled: boolean) {
  return `flex items-center gap-1 whitespace-nowrap rounded-md border px-3 py-2 text-sm font-label transition-colors duration-200 ${
    disabled
      ? "cursor-not-allowed border-background-200/60 text-foreground-200"
      : "cursor-pointer border-background-300/70 text-foreground-700 hover:border-primary-500/60 hover:text-primary-400"
  }`;
}

function pageClass(active: boolean) {
  return `flex h-9 min-w-9 cursor-pointer items-center justify-center rounded-md px-2 text-sm font-label transition-colors duration-200 ${
    active
      ? "bg-primary-500 text-background-50"
      : "border border-background-300/70 text-foreground-700 hover:border-primary-500/60 hover:text-primary-400"
  }`;
}

export default function Pagination({ current, total, onChange }: PaginationProps) {
  const [jump, setJump] = useState("");

  if (total <= 1) return null;

  const pages: (number | "ellipsis")[] = [];
  for (let index = 1; index <= total; index += 1) {
    if (index === 1 || index === total || Math.abs(index - current) <= 1) {
      pages.push(index);
    } else if (pages[pages.length - 1] !== "ellipsis") {
      pages.push("ellipsis");
    }
  }

  const go = (page: number) => {
    if (page < 1 || page > total || page === current) return;
    onChange(page);
  };

  const handleJump = (event: FormEvent) => {
    event.preventDefault();
    const target = Number(jump);
    if (Number.isNaN(target)) return;
    go(Math.min(Math.max(target, 1), total));
    setJump("");
  };

  return (
    <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row sm:justify-between">
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button type="button" onClick={() => go(current - 1)} disabled={current === 1} className={navClass(current === 1)}>
          <i className="ri-arrow-left-s-line" />
          上一页
        </button>
        {pages.map((page, index) =>
          page === "ellipsis" ? (
            <span key={`ellipsis-${index}`} className="px-1 text-sm text-foreground-500">
              …
            </span>
          ) : (
            <button key={page} type="button" onClick={() => go(page)} className={pageClass(page === current)}>
              {page}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => go(current + 1)}
          disabled={current === total}
          className={navClass(current === total)}
        >
          下一页
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      <form onSubmit={handleJump} className="flex items-center gap-2 text-xs text-foreground-500">
        跳至
        <input
          value={jump}
          onChange={(event) => setJump(event.target.value.replace(/\D/g, ""))}
          inputMode="numeric"
          aria-label="跳转到指定页"
          className="h-9 w-14 rounded-md border border-background-300/70 bg-background-100/80 text-center text-sm text-foreground-950 outline-none focus:border-primary-500/60"
        />
        页
        <button
          type="submit"
          className="cursor-pointer whitespace-nowrap rounded-md border border-background-300/70 px-3 py-2 text-sm text-foreground-700 transition-colors duration-200 hover:border-primary-500/60 hover:text-primary-400"
        >
          确定
        </button>
      </form>
    </div>
  );
}